/**
 * Alpine mixin：Tutorial / Troubleshooting 内容弹窗
 * 依赖组件提供 CONFIG.shop、CONFIG.token、formatRichText（可选）
 */
(function () {
  if (window.supportContentModalMixin) return;

  function isReferenceValue(val) {
    if (val == null || val === '') return false;
    const s = String(val).trim();
    return /^gid\s+/i.test(s) || s.includes('gid://shopify/');
  }

  function normalizeGid(val) {
    if (!val) return '';
    const s = String(val).trim().replace(/^gid\s+/i, '');
    if (s.startsWith('gid://')) return s;
    if (/^\d+$/.test(s)) return `gid://shopify/Page/${s}`;
    return s;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  const CONTENT_PAGE_URL_PARAM = 'support_page';

  window.supportContentModalMixin = function () {
    return {
      contentModal: { open: false, title: '', bodyHtml: '', loading: false },

      isReferenceValue,

      normalizeGid,

      escapeHtml,

      getContentPageIdFromUrl() {
        const raw = new URLSearchParams(window.location.search).get(CONTENT_PAGE_URL_PARAM);
        return raw ? decodeURIComponent(raw).trim() : '';
      },

      setContentPageIdInUrl(pageGid) {
        try {
          const url = new URL(window.location.href);
          if (pageGid) url.searchParams.set(CONTENT_PAGE_URL_PARAM, pageGid);
          else url.searchParams.delete(CONTENT_PAGE_URL_PARAM);
          history.replaceState(null, '', url.toString());
        } catch (e) {
          console.warn('setContentPageIdInUrl failed:', e);
        }
      },

      findItemByPageId(pageGid, allData) {
        const norm = normalizeGid(pageGid);
        if (!norm || !allData?.length) return null;
        return (
          allData.find((item) => {
            if (item.pageId && normalizeGid(item.pageId) === norm) return true;
            if (isReferenceValue(item.content) && normalizeGid(item.content) === norm) return true;
            return false;
          }) || null
        );
      },

      resolveItemBodyHtml(item) {
        const keys = ['body', 'description', 'answer', 'text'];
        const format = this.formatRichText?.bind(this);
        if (!format) return '';
        for (const key of keys) {
          if (!item[key] || isReferenceValue(item[key])) continue;
          const html = format(item[key]);
          if (html && html.trim() && !isReferenceValue(html)) return html;
        }
        return '';
      },

      resolveContentPageId(item) {
        if (item.pageId) return item.pageId;
        if (isReferenceValue(item.content)) return normalizeGid(item.content);
        return '';
      },

      async storefrontQuery(query) {
        const shop = this.CONFIG?.shop;
        const token = this.CONFIG?.token;
        if (!shop || !token) return {};
        const response = await fetch(`https://${shop}/api/2026-04/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query }),
        });
        return response.json();
      },

      async fetchArticleHtml(articleGid) {
        let id = normalizeGid(articleGid);
        if (!id) return '';
        if (!id.includes('Article')) {
          const num = id.replace(/\D/g, '') || articleGid;
          id = `gid://shopify/Article/${num}`;
        }
        try {
          const query = `{ article(id: "${id}") { contentHtml } }`;
          const json = await this.storefrontQuery(query);
          return json.data?.article?.contentHtml || '';
        } catch (e) {
          console.warn('fetchArticleHtml failed:', e);
          return '';
        }
      },

      async fetchPageHtml(pageGid) {
        const id = normalizeGid(pageGid);
        if (!id) return '';
        try {
          const query = `{ page(id: "${id}") { body } }`;
          const json = await this.storefrontQuery(query);
          const body = json.data?.page?.body;
          if (body && body.trim()) return body;
        } catch (e) {
          console.warn('fetchPageHtml failed:', e);
        }
        return '';
      },

      closeContentModal() {
        this.contentModal.open = false;
        this.contentModal.title = '';
        this.contentModal.bodyHtml = '';
        this.contentModal.loading = false;
        this.setContentPageIdInUrl('');
        if (!this.videoModal?.open) document.body.style.overflow = '';
      },

      async openContentModalForPageId(pageGid, options = {}) {
        const id = normalizeGid(pageGid);
        if (!id) return;
        if (typeof this.closeVideoModal === 'function') this.closeVideoModal();
        if (!options.fromUrl) this.setContentPageIdInUrl(id);
        this.contentModal.title = '';
        this.contentModal.bodyHtml = '';
        this.contentModal.loading = true;
        this.contentModal.open = true;
        document.body.style.overflow = 'hidden';
        try {
          const query = `{ page(id: "${id}") { title body } }`;
          const json = await this.storefrontQuery(query);
          const page = json.data?.page;
          this.contentModal.title = page?.title || 'Untitled';
          const html = page?.body?.trim()
            ? page.body
            : '<p class="support-content-modal__empty">No content available.</p>';
          this.contentModal.bodyHtml = html;
        } catch (e) {
          console.warn('openContentModalForPageId failed:', e);
          this.contentModal.title = 'Untitled';
          this.contentModal.bodyHtml =
            '<p class="support-content-modal__empty">Failed to load content.</p>';
        } finally {
          this.contentModal.loading = false;
        }
      },

      async openContentModalForItem(item, options = {}) {
        if (!item) return;
        if (typeof this.closeVideoModal === 'function') this.closeVideoModal();
        const pageGid = this.resolveContentPageId(item);
        if (!options.fromUrl && pageGid) this.setContentPageIdInUrl(pageGid);
        this.contentModal.title = item.title || item.question || 'Untitled';
        this.contentModal.bodyHtml = '';
        this.contentModal.loading = true;
        this.contentModal.open = true;
        document.body.style.overflow = 'hidden';
        try {
          let html = '';
          if (pageGid) html = await this.fetchPageHtml(pageGid);
          if (!html && item.articleId) html = await this.fetchArticleHtml(item.articleId);
          if (!html) html = this.resolveItemBodyHtml(item);
          if (!html) {
            html = '<p class="support-content-modal__empty">No content available.</p>';
          }
          this.contentModal.bodyHtml = html;
        } catch (e) {
          console.warn('openContentModalForItem failed:', e);
          this.contentModal.bodyHtml =
            '<p class="support-content-modal__empty">Failed to load content.</p>';
        } finally {
          this.contentModal.loading = false;
        }
      },

      async tryOpenContentModalFromUrl(allData = this.allData) {
        const pageGid = this.getContentPageIdFromUrl();
        if (!pageGid || this.contentModal.open) return;
        const item = this.findItemByPageId(pageGid, allData);
        if (item) {
          await this.openContentModalForItem(item, { fromUrl: true });
          return;
        }
        await this.openContentModalForPageId(pageGid, { fromUrl: true });
      },
    };
  };
})();
